/* ============================================================
   AUREA · Contenido + componentes compartidos
   ============================================================ */

const LAUNCH_DATE = new Date('2026-07-15T09:00:00-04:00'); // lanzamiento

/* ---- Logo ---- */
function Logo({
  variant = 'gold',
  height = 28,
  style = {}
}) {
  const cls = variant === 'white' ? 'aurea-logo aurea-logo--white' : 'aurea-logo';
  return /*#__PURE__*/React.createElement("img", {
    src: (window.AUREA_IMG || "") + "aurea-logo.png",
    alt: "Aurea",
    className: cls,
    style: {
      height,
      ...style
    }
  });
}

/* ---- Asterisco de marca (glifo simple) ---- */
function Aster({
  className = ''
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: 'aster ' + className,
    "aria-hidden": "true"
  }, "\u2733");
}

/* ---- Hook de aparición al hacer scroll ---- */
function useReveal() {
  React.useEffect(() => {
    const els = document.querySelectorAll('.reveal:not(.in)');
    if (!('IntersectionObserver' in window)) {
      els.forEach(e => e.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(ents => {
      ents.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px'
    });
    els.forEach(e => io.observe(e));
    return () => io.disconnect();
  });
}

/* ---- Countdown ---- */
function useCountdown(target) {
  const [t, setT] = React.useState(() => Math.max(0, target - Date.now()));
  React.useEffect(() => {
    const id = setInterval(() => setT(Math.max(0, target - Date.now())), 1000);
    return () => clearInterval(id);
  }, [target]);
  const d = Math.floor(t / 86400000);
  const h = Math.floor(t % 86400000 / 3600000);
  const m = Math.floor(t % 3600000 / 60000);
  const s = Math.floor(t % 60000 / 1000);
  return {
    d,
    h,
    m,
    s,
    done: t <= 0
  };
}
function Countdown({
  tone = 'light'
}) {
  const c = useCountdown(LAUNCH_DATE.getTime());
  const cell = (v, label) => /*#__PURE__*/React.createElement("div", {
    className: "cd-cell"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cd-num"
  }, String(v).padStart(2, '0')), /*#__PURE__*/React.createElement("span", {
    className: "cd-label"
  }, label));
  return /*#__PURE__*/React.createElement("div", {
    className: 'countdown countdown--' + tone
  }, cell(c.d, 'días'), /*#__PURE__*/React.createElement("span", {
    className: "cd-sep"
  }, ":"), cell(c.h, 'hrs'), /*#__PURE__*/React.createElement("span", {
    className: "cd-sep"
  }, ":"), cell(c.m, 'min'), /*#__PURE__*/React.createElement("span", {
    className: "cd-sep"
  }, ":"), cell(c.s, 'seg'));
}

/* ---- Klaviyo ---- */
const KLAVIYO_PUBLIC_KEY = 'UT6EAk';
const KLAVIYO_LIST_ID = 'VynHfn';

/* ---- Formulario lista de espera ---- */
function WaitlistForm({
  tone = 'light',
  compact = false,
  id = 'wl'
}) {
  const KEY = 'aurea_waitlist';
  const [email, setEmail] = React.useState('');
  const [done, setDone] = React.useState(() => {
    try {
      return !!localStorage.getItem(KEY);
    } catch (e) {
      return false;
    }
  });
  const [err, setErr] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const submit = async e => {
    e.preventDefault();
    const value = email.trim();
    const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
    if (!ok) {
      setErr('Ingresa un correo válido');
      return;
    }
    setErr('');
    setLoading(true);
    try {
      // Klaviyo — suscripción a lista (Client API, requiere Public API Key)
      await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: value
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: KLAVIYO_LIST_ID
                }
              }
            }
          }
        })
      });
    } catch (err) {
      // No bloqueamos la confirmación si la red falla
    }
    try {
      localStorage.setItem(KEY, value);
    } catch (e) {}
    setLoading(false);
    setDone(true);
  };
  if (done) {
    return /*#__PURE__*/React.createElement("div", {
      className: 'wl-done wl-done--' + tone
    }, /*#__PURE__*/React.createElement("span", {
      className: "wl-done-mark"
    }, "\u2733"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, "Est\xE1s en la Lista Dorada."), /*#__PURE__*/React.createElement("p", null, "Te escribiremos a tu correo con tu acceso anticipado y tu precio congelado. Revisa tu bandeja (y el spam, por si acaso).")));
  }
  return /*#__PURE__*/React.createElement("form", {
    className: 'wl wl--' + tone + (compact ? ' wl--compact' : ''),
    onSubmit: submit,
    noValidate: true
  }, /*#__PURE__*/React.createElement("div", {
    className: "wl-row"
  }, /*#__PURE__*/React.createElement("input", {
    className: "field wl-input",
    type: "email",
    inputMode: "email",
    autoComplete: "email",
    placeholder: "tucorreo@ejemplo.cl",
    value: email,
    "aria-label": "Correo electr\xF3nico",
    onChange: e => {
      setEmail(e.target.value);
      if (err) setErr('');
    }
  }), /*#__PURE__*/React.createElement("button", {
    className: 'btn ' + (tone === 'onGold' ? 'btn-ink' : tone === 'dark' ? 'btn-gold' : 'btn-gold'),
    type: "submit",
    disabled: loading
  }, loading ? 'Enviando…' : 'Quiero acceso anticipado')), err ? /*#__PURE__*/React.createElement("span", {
    className: "wl-err"
  }, err) : null);
}

/* ============================================================
   DATOS
   ============================================================ */

const NAV = [{
  href: '#promesa',
  label: 'La promesa'
}, {
  href: '#beneficios',
  label: 'Beneficios'
}, {
  href: '#formula',
  label: 'Fórmula'
}, {
  href: '#regalo',
  label: 'El regalo'
}, {
  href: '#preguntas',
  label: 'Preguntas'
}];
const BENEFITS = [{
  k: '01',
  t: 'Menos hinchazón',
  d: 'Una mezcla de 14 cepas probióticas y fibra vegetal que acompaña a tu microbiota para que tu abdomen se sienta más liviano día a día.'
}, {
  k: '02',
  t: 'Digestión en calma',
  d: 'Hongos adaptógenos y greens pensados para apoyar una digestión más serena, sin esa pesadez después de comer.'
}, {
  k: '03',
  t: 'Mente clara',
  d: 'El eje intestino–cerebro es real. Cuando tu interior está en equilibrio, la cabeza también respira.'
}, {
  k: '04',
  t: 'Defensas y brillo',
  d: 'Wellmune® betaglucano y astaxantina, dos activos premium que apoyan tus defensas y tu vitalidad desde adentro.'
}];
const FORMULA = [{
  t: '14 cepas probióticas',
  d: 'Una comunidad viva que repuebla y equilibra tu microbiota intestinal.'
}, {
  t: 'Hongos adaptógenos',
  d: 'Apoyan la respuesta al estrés y la sensación de calma sostenida.'
}, {
  t: 'Astaxantina',
  d: 'Antioxidante de origen marino, asociado a vitalidad y belleza de la piel.'
}, {
  t: 'Wellmune® Betaglucano',
  d: 'Activo clínicamente estudiado para apoyar el sistema inmune.'
}, {
  t: 'Greens & polifenoles',
  d: 'Concentrado verde rico en compuestos vegetales protectores.'
}, {
  t: 'Fibra vegetal',
  d: 'Alimenta a tus probióticos y favorece el tránsito natural.'
}];
const FACTS = [{
  n: '14',
  l: 'cepas probióticas'
}, {
  n: '30',
  l: 'porciones de 7,79 g'
}, {
  n: 'Tropical',
  l: 'maracuyá & piña'
}, {
  n: '0',
  l: 'azúcar · solo stevia'
}];
const RITUAL = [{
  n: '01',
  t: 'Sirve',
  d: 'Agrega una porción (7,79 g) a 200–250 ml de agua fría en tu termo mezclador AUREA.'
}, {
  n: '02',
  t: 'Agita',
  d: 'Tapa y agita 10 segundos. Verás cómo se disuelve en un dorado suave de maracuyá y piña.'
}, {
  n: '03',
  t: 'Presencia',
  d: 'Bébelo en ayunas o cuando lo necesites. Un minuto para ti, todos los días.'
}];
const TESTIMONIALS = [{
  q: 'Por primera vez en años no termino el día con el botón del pantalón apretado. Es sutil, pero lo notas.',
  a: 'Catalina R.',
  m: 'Beta tester · Santiago'
}, {
  q: 'El sabor maracuyá–piña es lo que me hace tomarlo sin falta. No sabe a “remedio”, sabe a ritual.',
  a: 'Javiera M.',
  m: 'Beta tester · Viña del Mar'
}, {
  q: 'Lo que más me gustó fue sentir la panza más liviana y la cabeza más tranquila a media mañana.',
  a: 'Fernanda T.',
  m: 'Beta tester · Providencia'
}];
const FAQS = [{
  q: '¿Qué es AUREA Greens & Superfoods?',
  a: 'Es un batido diario de greens y superfoods formulado con una cepa de 14 probióticos, hongos adaptógenos, astaxantina, Wellmune® betaglucano, greens y polifenoles. Está pensado para acompañar tu bienestar digestivo y mental, con un sabor 90% maracuyá y 10% piña, endulzado solo con stevia.'
}, {
  q: '¿Qué gano si me sumo a la Lista Dorada?',
  a: 'Dos cosas: (1) acceso al checkout 24 horas antes que el público general y (2) un precio exclusivo de lanzamiento que queda congelado para ti por 24 meses. Te avisamos por correo apenas abramos.'
}, {
  q: '¿Cuándo es el lanzamiento?',
  a: 'Estamos afinando los últimos detalles. Quienes estén en la Lista Dorada serán los primeros en enterarse de la fecha y los primeros en poder comprar.'
}, {
  q: '¿Qué incluye el regalo de suscripción?',
  a: 'Al suscribirte recibes de regalo un termo mezclador AUREA en color blanco y un frasco de Vitamina D3+K2 (30 cápsulas) para complementar tu ritual de bienestar.'
}, {
  q: '¿Cómo se toma y a qué sabe?',
  a: 'Una porción de 7,79 g disuelta en agua fría, idealmente en tu termo mezclador. Sabe a maracuyá con un toque de piña, suave y refrescante. Sin azúcar: endulzado solo con stevia.'
}, {
  q: '¿Puedo cancelar la suscripción cuando quiera?',
  a: 'Sí. La suscripción es flexible: puedes pausarla o cancelarla cuando lo necesites desde tu cuenta. Tu precio de lanzamiento se mantiene mientras tu suscripción siga activa.'
}, {
  q: '¿AUREA reemplaza una alimentación equilibrada?',
  a: 'No. AUREA es un suplemento alimentario que acompaña hábitos saludables; no reemplaza una alimentación balanceada ni un tratamiento médico. Ante dudas, consulta con un profesional de la salud.'
}];
const PRICING = [{
  plan: 'Compra única',
  launch: '$79.990',
  normal: '$89.990',
  per: 'pago único',
  note: 'Tu frasco de 30 porciones.'
}, {
  plan: 'Suscripción mensual',
  launch: '$69.990',
  normal: '$79.990',
  per: '/ mes',
  note: 'Incluye el regalo de bienvenida.',
  featured: true
}, {
  plan: 'Suscripción trimestral',
  launch: '$178.474',
  normal: '$203.974',
  per: '/ cada 3 meses',
  note: 'El mejor valor por porción.'
}];
Object.assign(window, {
  LAUNCH_DATE,
  Logo,
  Aster,
  useReveal,
  useCountdown,
  Countdown,
  WaitlistForm,
  NAV,
  BENEFITS,
  FORMULA,
  FACTS,
  RITUAL,
  TESTIMONIALS,
  FAQS,
  PRICING
});